import { BidDecisionGroup, CreateSiteVisitInput, Customer, SiteVisit } from "@/graphql/__generated__/graphql";
import { Dispatch, JSX, SetStateAction, useEffect, useState } from "react";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { MobileServiceDefaultValues, MobileServiceSchema, TMobileServiceSchema } from "../../Models/MobileService";
import { Resolver, SubmitHandler, useForm } from "react-hook-form";
import { SiteVisitDefaultValues, TSiteVisitSchema } from "../../Models/SiteVisit";
import { appendOpOrgIdParams, convertDecimalToBp } from "@/utils/helpers";
import { isEqual, merge } from "lodash";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import AddSiteVisitComponent from "../../SiteVisits/SiteVisitsComponent";
import { Button } from "@/components/ui/button";
import { CustomCombobox } from "@/components/shared";
import { FormattedNumberInputField } from "@/components/shared";
import { InformationCircleIcon } from "@heroicons/react/24/outline";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { NewSiteComponent } from "../../Components";
import { SubmitAction } from "@/constants/enums";
import { Switch } from "@/components/ui/switch";
import { TSiteSchema } from "../../Models/Site";
import { Textarea } from "@/components/ui/textarea";
import { appendSiteAddress } from "@/utils/helpers";
import { cn } from "@/lib/utils";
import { prefillMobileServiceWithMockData } from "./mocks";
import { useAddMobileServiceMutationHook } from "@/hooks";
import { useTranslation } from "react-i18next";
import useUserStore from "@/store/shield";
import { zodResolver } from "@hookform/resolvers/zod";

interface Props {
  customer: Customer;
  biddingOperationalOrganizations: Partial<BidDecisionGroup>[];
  setIsLoading: Dispatch<SetStateAction<boolean>>;
};

/**
 * AddMobileService form component
 *
 * @param {Props} props
 *
 * @returns {JSX.Element}
 */
const AddMobileService = (props: Props): JSX.Element => {

  // Props
  const { customer, biddingOperationalOrganizations, setIsLoading } = props;

  // Hooks
  const navigate = useNavigate();

  const { t } = useTranslation(["translation", "components"]);
  const { tenderID: currentTenderID } = useParams();
  const [searchParams] = useSearchParams();
  const currentOperationalOrganizationID = searchParams.get("operationalOrganizationId");

  const onInvalid = (errors: unknown) => console.error(errors);

  const form = useForm<TMobileServiceSchema>({
    resolver: zodResolver(MobileServiceSchema) as Resolver<TMobileServiceSchema>,
    defaultValues: {
      ...MobileServiceDefaultValues,
      operationalOrganizationId: currentOperationalOrganizationID ? parseInt(currentOperationalOrganizationID) : undefined,
    },
    mode: "onChange"
  });

  const { control, register, watch, reset, handleSubmit, setError,
    setValue, getValues, trigger, formState: { errors } } = form;

  // States
  const [newSite, setNewSite] = useState<boolean>(false);
  const [siteVisitsToDisplay, setSiteVisitsToDisplay] = useState<CreateSiteVisitInput[] | []>([]);
  const [showRemoveSiteVisitAlert, setShowRemoveSiteVisitAlert] = useState<boolean>(false);

  // Mutations
  const { addMobileServiceMutation, loading: addMobileServiceLoading } = useAddMobileServiceMutationHook();

  /**
   * OnSubmit handler for the addMobileServiceMutation
   * @param {TMobileServiceSchema} data
   * @returns
   */
  const onSubmit: SubmitHandler<TMobileServiceSchema> = (data: TMobileServiceSchema): void => {
    const { siteId, siteInput } = data;

    // Check for missing site
    if ((!siteId || siteId === 0) && !siteInput) {
      // Manually set errors for siteId and siteInput
      setError("siteId", {
        type: "manual",
        message: t("components:newsite.errors.siteid")
      });
      setError("siteInput", {
        type: "manual",
        message: t("components:newsite.errors.siteinput")
      });
      return;
    } else if (siteId && siteId !== 0) {
      // If siteId is present, remove siteInput
      delete data.siteInput;
    } else if (!siteId && siteInput) {
      // If siteInput is present, remove siteId
      delete data.siteId;
    }

    // convert siteVisits' margin to BP and dates to ISO strings
    const modifiedSiteVisits: CreateSiteVisitInput[] = data.siteVisits.map((siteVisit) => {
      const { startDate, endDate, ...rest } = siteVisit;
      return {
        ...rest,
        margin: convertDecimalToBp(siteVisit.margin),
        ...(startDate && { startDate: startDate.toISOString() }),
        ...(endDate && { endDate: endDate.toISOString() }),
      };
    });

    try {
      addMobileServiceMutation({
        variables: {
          tenderId: Number(currentTenderID),
          input: {
            ...data,
            nonTariffAllowance: data.nonTariffAllowance ?? 0,
            siteVisits: modifiedSiteVisits,
          },
        }
      }).then((res) => {
        // Only proceed if there are no errors
        if (!res.errors) {
          // Reset form after submit success
          reset(MobileServiceDefaultValues);
          // Redirect to the services tab
          navigate(`/tender/${currentTenderID}/services/all${appendOpOrgIdParams(currentOperationalOrganizationID)}`);
        }
      });
    } catch (err) {
      console.error("Error occurred:", err);
    }
  };

  /**
   * Handler function to add a new site to the form state
   * @param {TSiteSchema} siteData
   */
  const handleAddSite = (siteData: TSiteSchema) => {
    setValue("siteInput", siteData);
  };

  /**
   * Handler function to remove a site from the form state.
   * Resets the siteId to 0 and sets newSite to false.
   * @returns {void}
   */
  const handleRemoveSite = (): void => {
    setValue("siteId", 0);
    setValue("siteInput", undefined);
    setNewSite(false);
  };

  /**
   * Handler function to add a new siteVisit to the form state
   * @param {TSiteVisitSchema} siteVisitData
   */
  const handleAddSiteVisit = async (siteVisitData: TSiteVisitSchema, action: SubmitAction, setShowSiteVisitsModal: Dispatch<SetStateAction<boolean>>) => {
    const currentSiteVisits = getValues("siteVisits") ?? [];
    // We merge the objects to add the fields that were not edited
    const newSiteVisitObject = merge({}, SiteVisitDefaultValues, siteVisitData);
    setValue("siteVisits", [...currentSiteVisits, newSiteVisitObject]);

    trigger("siteVisits");

    if (action === SubmitAction.AddAndClose) {
      setShowSiteVisitsModal(false);
    }
  };

  /**
   * Handler function to remove a siteVisit from the form state.
  */
  const handleRemoveSiteVisit = async (siteVisit: SiteVisit) => {
    // Get the current list of siteVisits from the form state
    const currentSiteVisits = getValues!("siteVisits");

    // Filter out the siteVisit that needs to be removed by deep comparing the objects.
    const updatedSiteVisits = currentSiteVisits.filter(
      // (s: SiteVisit) => !isEqual(s, siteVisit)
      (s) => !isEqual(s, siteVisit)
    );

    // Update the form state with the new list of siteVisit
    setValue!("siteVisits", updatedSiteVisits);

    // Close the alert dialog
    setShowRemoveSiteVisitAlert(false);
  };

  const handleEditSiteVisit = async (siteVisitData: TSiteVisitSchema, selectedSiteVisit: SiteVisit, setShowEditSiteVisitModal: Dispatch<SetStateAction<boolean>>) => {
    const currentSiteVisits = getValues("siteVisits") ?? [];
    // We merge the objects to add the fields that were not edited
    const newSiteVisitObject = merge({}, SiteVisitDefaultValues, siteVisitData);
    // Replace the existing site visit with the updated one
    const updatedSiteVisits = currentSiteVisits.map(siteVisit =>
      isEqual(siteVisit, selectedSiteVisit) ? newSiteVisitObject : siteVisit
    );
    setValue("siteVisits", updatedSiteVisits);
    trigger("siteVisits");

    setShowEditSiteVisitModal(false);
  };

  /**
   * Keeps the array of siteVisits in sync with the form
   */
  useEffect(() => {
    setSiteVisitsToDisplay(getValues("siteVisits") as CreateSiteVisitInput[] ?? []);
  }, [watch("siteVisits")]);

  // Auto-assign operational organization if there's only one option
  useEffect(() => {
    if (biddingOperationalOrganizations.length === 1) {
      const singleOrg = biddingOperationalOrganizations[0];
      if (singleOrg.operationalOrganizationId) {
        setValue("operationalOrganizationId", Number(singleOrg.operationalOrganizationId));
        trigger("operationalOrganizationId");
      }
    }
  }, [biddingOperationalOrganizations, setValue, trigger]);

  useEffect(() => {
    setIsLoading(addMobileServiceLoading);
  }, [addMobileServiceLoading]);

  // Prefill mock data for testing
  const handlePrefillMockData = () => {
    const currentOrgId = useUserStore.getState().orgId;
    prefillMobileServiceWithMockData(form, customer, biddingOperationalOrganizations, currentOrgId);
  };

  // Add event listener for prefill button
  useEffect(() => {
    const formElement = document.getElementById("add-mobile-service-form");
    if (formElement) {
      const handlePrefillEvent = () => {
        handlePrefillMockData();
      };

      formElement.addEventListener("prefillMockData", handlePrefillEvent);

      return () => {
        formElement.removeEventListener("prefillMockData", handlePrefillEvent);
      };
    }
  }, [biddingOperationalOrganizations, customer]);

  return (
    <>
      {/* Service form */}
      <form className="space-y-6" id="add-mobile-service-form" onSubmit={handleSubmit(onSubmit, onInvalid)}>
        <div className="flex-col-4">
          <div className="flex-wrap items-start gap-4 flex justify-between">
            {/* Name */}
            <div className="responsive-col">
              <Label htmlFor="name" className={cn(errors.name && "text-red-500")}>
                {t("mobileservice.inputs.name.label")}
              </Label>
              <Input {...register("name")} type="text" autoFocus placeholder={t("mobileservice.inputs.name.placeholder")} />
              {errors.name && (
                <p className="input-error-message">
                  {errors.name.message as string}
                </p>
              )}
            </div>

            {/* Operational Organization */}
            <div className="responsive-col">
              <Label htmlFor="operationalorganization" className={cn(errors.name && "text-red-500")}>
                {t("mobileservice.inputs.operationalorganization.label")}
              </Label>
              <CustomCombobox
                options={biddingOperationalOrganizations.map((group) => ({
                  label: group.operationalOrganizationName,
                  value: Number(group.operationalOrganizationId)
                }))}
                value={watch("operationalOrganizationId")}
                onChange={(value) => {
                  setValue("operationalOrganizationId", Number(value));
                  trigger("operationalOrganizationId");
                }}
                placeholder={t("mobileservice.inputs.operationalorganization.selectplaceholder")}
                error={!!errors.operationalOrganizationId}
              />
              {errors.operationalOrganizationId && (
                <p className="input-error-message">
                  {errors.operationalOrganizationId.message as string}
                </p>
              )}
            </div>

            {/* Hourly Wage */}
            <div className="responsive-col">
              <Label htmlFor="hourlyWage" className={cn(errors.hourlyWage && "text-red-500")}>
                {t("mobileservice.inputs.hourlywage.label")}
              </Label>
              <FormattedNumberInputField
                name="hourlyWage"
                control={control}
                additionalSuffix="/h"
                placeholder={t("mobileservice.inputs.hourlywage.placeholder")}
                style="currency"
              />
              {errors.hourlyWage && (
                <p className="input-error-message">
                  {errors.hourlyWage.message as string}
                </p>
              )}
            </div>

            {/* Non-Tariff Allowance */}
            <div className="responsive-col">
              <Label htmlFor="nonTariffAllowance" className={cn(errors.nonTariffAllowance && "text-red-500")}>
                {t("mobileservice.inputs.nontariffallowance.label")}
              </Label>
              <FormattedNumberInputField
                name="nonTariffAllowance"
                control={control}
                additionalSuffix="/h"
                placeholder={t("mobileservice.inputs.nontariffallowance.placeholder")}
                style="currency"
              />
              {errors.nonTariffAllowance && (
                <p className="input-error-message">
                  {errors.nonTariffAllowance.message as string}
                </p>
              )}
            </div>

            {/* Hours Briefing per Employee */}
            <div className="responsive-col">
              <Label htmlFor="hoursBriefingPerEmployee" className={cn(errors.hoursBriefingPerEmployee && "text-red-500")}>
                {t("mobileservice.inputs.hoursbriefingperemployee.label")}
              </Label>
              <FormattedNumberInputField
                name="hoursBriefingPerEmployee"
                control={control}
                placeholder={t("mobileservice.inputs.hoursbriefingperemployee.placeholder")}
                additionalSuffix={t("mobileservice.inputs.hoursbriefingperemployee.suffix")}
                spaceBetweenSuffix
                isInteger
              />
              {errors.hoursBriefingPerEmployee && (
                <p className="input-error-message">
                  {errors.hoursBriefingPerEmployee.message as string}
                </p>
              )}
            </div>

            {/* Travel Time */}
            <div className="responsive-col">
              <Label htmlFor="travelTime" className={cn(errors.travelTime && "text-red-500")}>
                {t("mobileservice.inputs.traveltime.label")}
              </Label>
              <FormattedNumberInputField
                name="travelTime"
                control={control}
                placeholder={t("mobileservice.inputs.traveltime.placeholder")}
                additionalSuffix={t("mobileservice.inputs.traveltime.suffix")}
                spaceBetweenSuffix
                isInteger
              />
              {errors.travelTime && (
                <p className="input-error-message">
                  {errors.travelTime.message as string}
                </p>
              )}
            </div>

            {/* Pay Group */}
            {/* <div className="w-full md:max-w-[49%] md:flex-1 flex flex-col gap-2">
              <Label htmlFor="payGroup" className={cn(errors.payGroup && "text-red-500")}>
                {t("mobileservice.inputs.paygroup.label")}
              </Label>
              <Select onValueChange={(value) => {setValue("payGroup", value as "Group 1" | "Group 2" | "Group 3"); trigger("payGroup");}} >
                <SelectTrigger id="payGroup">
                  <SelectValue placeholder={t("mobileservice.inputs.paygroup.selectplaceholder")} />
                </SelectTrigger>
                <SelectContent>
                  {["Group 1", "Group 2", "Group 3"].map((group) => (
                    <SelectItem key={group} value={group}>
                      {toNormalCase(group)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.payGroup && (
                <p className="input-error-message">
                  {errors.payGroup.message as string}
                </p>
              )}
            </div> */}

            {/* Additional Service*/}
            <div className="flex flex-col gap-2">
              <Label htmlFor="additional" className="flex items-start">
                {t("mobileservice.inputs.additional.label")}
                {/* Additional */}
                <HoverCard>
                  <HoverCardTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-5 w-5 p-0 hover:cursor-help ml-1 items-start"
                      type="button"
                    >
                      <InformationCircleIcon className="h-5 w-5" />
                    </Button>
                  </HoverCardTrigger>
                  <HoverCardContent>
                    <p className="max-w-[320px]">
                      {t("mobileservice.inputs.additional.note")}
                    </p>
                  </HoverCardContent>
                </HoverCard>
              </Label>
              <Switch
                checked={watch("additional")}
                {...register("additional")}
                onCheckedChange={(checked) => {
                  setValue("additional", checked);
                }}
              />
            </div>
          </div>

          {/* Info */}
          <div className="flex flex-col gap-2">
            <Label htmlFor="info" className={cn(errors.info && "text-red-500")}>
              {t("permanentservice.inputs.info.label")}
            </Label>
            <Textarea
              {...register("info")}
              placeholder={t("permanentservice.inputs.info.placeholder")}
              className={cn("max-h-[200px]", errors.info && "border-red-500")}
            />
            {errors.info && (
              <p className="input-error-message">
                {errors.info.message as string}
              </p>
            )}
          </div>

          <div className="w-full">
            {/* Site ID */}
            <div className="flex flex-row gap-2 items-start">
              <div className="flex-1 flex flex-col gap-2">
                <Label htmlFor="siteId" className={cn(errors.siteId && "text-red-500")}>
                  {t("mobileservice.inputs.site.label")}
                </Label>
                <CustomCombobox
                  disabled={newSite}
                  options={customer?.sites.map((site) => ({
                    label: (
                      <>
                        {site.name}
                        <span className="text-xs text-gray-500"> - {appendSiteAddress(site)}</span>
                      </>
                    ),
                    value: site.id,
                    searchValue: `${site.name} ${appendSiteAddress(site)}`
                  })) ?? []}
                  value={watch("siteId")}
                  onChange={(value) => {
                    setValue("siteId", Number(value));
                    trigger("siteId");
                  }}
                  placeholder={t("mobileservice.inputs.site.selectplaceholder")}
                  error={!!errors.siteId}
                />
                {errors.siteId && (
                  <p className="input-error-message">
                    {errors.siteId.message as string}
                  </p>
                )}
              </div>

              <Button
                type="button"
                variant="outline"
                disabled={newSite}
                onClick={() => {
                  setValue("siteId", 0);
                  setNewSite(true);
                }}
                className="mt-5.5"
              >
                {t("mobileservice.inputs.site.btnnewsite")}
              </Button>
            </div>
          </div>

          {newSite && <NewSiteComponent setNewSite={setNewSite} onAddSite={handleAddSite} onRemoveSite={handleRemoveSite} />}
        </div>
      </form>
      {/* End of Mobile Service form */}

      <div className="mt-4">
        {errors.siteVisits && (
          <p className="input-error-message -mb-2">
            {errors.siteVisits.message as string}
          </p>
        )}
        {/* SiteVisit Section */}
        <AddSiteVisitComponent
          onAddSiteVisit={handleAddSiteVisit}
          onRemoveSiteVisit={handleRemoveSiteVisit}
          onEditSiteVisit={handleEditSiteVisit}
          siteVisitsToDisplay={siteVisitsToDisplay}
          setShowRemoveSiteVisitAlert={setShowRemoveSiteVisitAlert}
          showRemoveSiteVisitAlert={showRemoveSiteVisitAlert}
        />
      </div>
    </>
  );
};

export default AddMobileService;
